import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  ChevronUp,
  BarChart3,
  ThumbsUp,
  ThumbsDown,
  CheckCircle2,
} from "lucide-react";
import { StockCall } from "../types";
import {
  Sword,
  BarChart2,
  LogIn,
  Target,
  ShieldAlert,
  LogOut,
} from "lucide-react";
import { supabase } from "../lib/supabase";
import { userInfo } from "os";
import { profile } from "console";
import { useAuth } from "../contexts/AuthContext";
import Profile from "../pages/Profile";


interface StockCardProps {
  callGroup: StockCall[];
}

const StockCard: React.FC<StockCardProps> = ({ callGroup }) => {
  const [expanded, setExpanded] = useState(true);
  const [voted, setVoted] = useState<"profit" | "loss" | null>(null); // Local state for user vote
  const { profile, signOut } = useAuth();

  const primaryCall = callGroup[0];
  const createdOn = new Date(primaryCall.createdOn).toLocaleTimeString(
    "en-IN",
    {
      hour: "2-digit",
      minute: "2-digit",
    },
  );

  const groupTargetAchieved = callGroup.some(
    (call) =>
      call.exitPrice &&
      call.targetPrice &&
      Number(call.exitPrice) >= Number(call.targetPrice),
  );

  const handleToggle = () => setExpanded((s) => !s);

 const handleVote = async (type: "profit" | "loss") => {
  if (!profile) return;

  const { data, error, count } = await supabase
    .from("Vitta_Calls_Stats")
    .insert({
      call_id: primaryCall.id,
      user_id: profile.id, // <-- supply the logged-in user's id
      profit: type,     // optional: store the vote type
    });

  if (error) {
    console.error("Error inserting vote:", error);
  } else {
    console.log("Inserted rows:", data, "Count:", count);
  } 
};

  

  return (
    <motion.div
      layout
      className="bg-dark-card rounded-xl border border-dark-border overflow-hidden backdrop-blur-sm hover:shadow-xl transition"
    >
      {/* Header */}
      <div
        onClick={handleToggle}
        className="p-3 bg-gradient-to-r from-dark-surface to-dark-card border-b border-dark-border cursor-pointer flex justify-between items-center"
      >
        <div>
          <h3 className="text-sm font-semibold text-white">
            {primaryCall.instrumentType === "options"
              ? "Options Group"
              : primaryCall.instrumentName}
          </h3>
          <p className="text-[11px] text-accent-primary">
            {primaryCall.instrumentType}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-gray-400">{createdOn}</span>
          <span className="text-[11px] px-2 py-0.5 bg-accent-primary/20 text-accent-primary rounded-full font-bold">
            {callGroup.length} {callGroup.length === 1 ? "Call" : "Calls"}
          </span>
          {expanded ? (
            <ChevronUp size={14} className="text-gray-400" />
          ) : (
            <ChevronDown size={14} className="text-gray-400" />
          )}
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="p-3 bg-dark-surface space-y-3">
              <div className="overflow-x-auto">
                <table className="w-full text-[11px] text-left border-collapse">
                  <thead className="text-gray-400 uppercase">
                    <tr>
                      <th className="px-2 py-1 text-center">
                        <Sword size={14} />
                      </th>
                      <th className="px-2 py-1 text-center">
                        <BarChart2 size={14} />
                      </th>
                      <th className="px-2 py-1 text-center">
                        <LogIn size={14} />
                      </th>
                      <th className="px-2 py-1 text-center">
                        <Target size={14} />
                      </th>
                      <th className="px-2 py-1 text-center">
                        <ShieldAlert size={14} />
                      </th>
                      <th className="px-2 py-1 text-center">
                        <LogOut size={14} />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {callGroup.map((call) => (
                      <tr
                        key={call.id}
                        className="border-t border-dark-border text-white"
                      >
                        <td className="px-2 py-1 font-bold text-green-400">
                          {call.action}
                        </td>
                        <td className="px-2 py-1">{call.instrumentName}</td>
                        <td className="px-2 py-1">₹{call.entryPrice}</td>
                        <td className="px-2 py-1">
                          ₹{call.targetPrice ?? "—"}
                        </td>
                        <td className="px-2 py-1">₹{call.stoploss ?? "—"}</td>
                        <td className="px-2 py-1 text-blue-400">
                          {call.exitPrice ? `₹${call.exitPrice}` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {groupTargetAchieved && (
                <div className="bg-green-600/20 text-green-400 text-[11px] font-bold text-center py-1.5 rounded-md border border-green-500/20">
                  ⭐ Vytta Strikes!
                </div>
              )}

              {primaryCall.pastComments &&
                primaryCall.pastComments.length > 0 && (
                  <div className="bg-dark-card rounded-md border border-dark-border p-2 text-[11px] text-gray-300">
                    <p className="font-semibold text-green-400 mb-1 italic">
                      Vytta Says
                    </p>
                    <div className="space-y-1 max-h-24 overflow-y-auto">
                      {primaryCall.pastComments}
                    </div>
                  </div>
                )}

              {/* OPINION POLL SECTION */}
              {!voted && groupTargetAchieved ? (
                <div className="flex flex-col items-center gap-3">
                  <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-gray-500">
                    <BarChart3 size={12} className="text-orange-500" />
                    Did you make a profit?
                  </div>
                  <div className="flex w-full gap-2">
                    <button
                      onClick={() => handleVote("profit")}
                      className="flex-1 py-2 bg-green-500/10 hover:bg-green-500/20 border border-green-500/20 rounded-lg text-green-500 text-[10px] font-black uppercase flex items-center justify-center gap-2 transition-all"
                    >
                      <ThumbsUp size={12} /> Yes
                    </button>
                    <button
                      onClick={() => handleVote("loss")}
                      className="flex-1 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 rounded-lg text-red-500 text-[10px] font-black uppercase flex items-center justify-center gap-2 transition-all"
                    >
                      <ThumbsDown size={12} /> No
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-3 py-1">
                  <CheckCircle2 size={16} className="text-orange-500" />
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white italic">
                    Response Awaited
                  </span>
                
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default StockCard;
